<script context="module" lang="ts">
  import { cva } from 'class-variance-authority';

  const DESKTOP_ALT = 'desktop-only' as const;
  const getSrc = (sources: ShopifyVideo['sources']) => {
    const hd = sources.find((src) => src.url.includes('HD-720p'))?.url;
    if (hd) {
      return hd;
    } else {
      const mp4 = sources.find((src) => src.url.includes('.mp4'))?.url;
      return mp4;
    }
  };

  const flatOptions = (options: ProductCardFragment['options']) =>
    options?.map((opt) => opt.values.map((val) => val)).flat();

  const getVariantOptions = ({
    options,
    images
  }: {
    options: ProductCardFragment['options'];
    images: ShopifyImage[];
  }) => {
    const allOptions = flatOptions(options);

    if (allOptions) {
      return [
        ...new Set([
          ...(images || [])
            .filter((img) => img.altText && allOptions.includes(img.altText))
            .map((img) => img.altText)
        ])
      ].filter((img) => !!img);
    } else return;
  };

  const host = cva(['group relative isolate aspect-[2/3] overflow-hidden h-full w-full'], {
    variants: {
      size: {
        xs: 'aspect-[7/9]',
        sm: 'aspect-[2/3] lg:aspect-[3/5]',
        lg: 'aspect-[2/3] lg:aspect-[6/5]'
      }
    }
  });

  const sliderStyle = cva([], {
    variants: {
      size: {
        xs: 'm-2.5',
        sm: 'm-2.5 lg:m-3',
        lg: 'm-2.5 lg:m-3'
      }
    }
  });

  const colorSelectorStyle = cva(['absolute max-h-[65%]'], {
    variants: {
      size: {
        xs: 'bottom-[calc(10px+46px)] right-2.5',
        sm: 'bottom-[calc(10px+52px)] right-2.5 lg:bottom-[calc(12px+59px)] lg:right-3',
        lg: 'bottom-[calc(10px+52px)] right-2.5 lg:bottom-[calc(12px+59px)] lg:right-3'
      }
    }
  });

  const infoContainerStyle = cva(
    ['flex w-full justify-between overflow-hidden bg-highlight/64 backdrop-blur'],
    {
      variants: {
        size: {
          xs: 'p-2.5 h-[46px]',
          sm: 'p-2.5 lg:p-3',
          lg: 'p-2.5 lg:p-3'
        }
      }
    }
  );

  const typographyStyle = cva(['whitespace-normal'], {
    variants: {
      size: {
        xs: 'text-xs lg:text-sm lg:font-semibold font-semibold',
        sm: 'text-sm font-semibold lg:text-md lg:font-semibold',
        lg: 'text-sm font-semibold lg:text-md lg:font-semibold'
      }
    }
  });

  const sliderButtonStyle = cva(
    [
      'pointer-events-none !opacity-0 focus-visible:!opacity-100 group-hover:pointer-events-auto group-hover:!opacity-100 z-20'
    ],
    {
      variants: {
        size: { xs: 'mb-[46px]', sm: 'mb-[52px] lg:mb-16', lg: 'mb-[52px] lg:mb-16' },
        galleryOnly: { true: 'mb-0' }
      }
    }
  );
</script>

<script lang="ts">
  import { fade } from 'svelte/transition';

  import Slider from './slider.svelte';
  import Accordion from './accordion.svelte';
  import ColorSelector from './color-selector.svelte';
  import SliderButton from './ui/slider-button.svelte';
  import ShopifyImg from './shopify-img.svelte';

  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { buildSizes } from '$lib/image';
  import { sitewideSale } from '$lib/stores/content';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { formatCurrency } from '$lib/utils/currency';
  import { snapScrollPosition } from '$lib/actions/snap-scroll-position';
  import { intersectionObserver } from '$lib/actions/intersection-observer';
  import { aboveLg } from '$lib/stores/breakpoints';

  import type { ShopifyImage, ShopifyVideo } from '$types/shopify';
  import type { CurrencyCode } from '$gql/storefront/schemas';
  import type { ImageDescriptionStoryblok } from '$types/bloks';
  import type { ProductCardFragment } from '$gql/storefront/operations';
  import Link from './ui/link.svelte';
  import PreviewMedia from './pdp/preview-media.svelte';
  import Icon from './ui/icon/icon.svelte';
  import Skeleton from './ui/skeleton.svelte';
  import { abTestVersion } from '$lib/stores/ab-test-version';

  let selectedMedia: ShopifyImage | string | undefined = undefined;
  let selectedMediaIdx: number | undefined = undefined;
  let className: string | undefined = undefined;
  export { className as class };
  export let images: ShopifyImage[];
  export let media: (ShopifyImage | ShopifyVideo)[] | undefined = undefined;
  export let size: 'xs' | 'sm' | 'lg' = 'sm';
  export let href: string | undefined = undefined;
  export let currencyCode: CurrencyCode | undefined = undefined;
  export let title: string | undefined = undefined;
  export let category: string | undefined = undefined;
  export let isOnSale = false;
  export let price: string | undefined = undefined;
  export let compareAtPrice: string | undefined = undefined;
  export let imageDescription: ImageDescriptionStoryblok[] | undefined = undefined;
  export let onImageView: ((n: number) => void) | undefined = undefined;
  export let onImageDescriptionInteract: ((title: string) => void) | undefined = undefined;
  export let containFirst = false;
  export let isSitewideSale = false;
  export let isHighlight = false;
  export let isPdp = false;
  export let variantColor: ProductCardFragment['metafields'][number] | undefined = undefined;
  export let options: ProductCardFragment['options'];
  export let showVideoButton: boolean | undefined = undefined;
  export let ATestVersionMediaIndexes: number[] | undefined = [];

  const getWidth = () => {
    if (isPdp) {
      return 1000;
    } else if (isHighlight) {
      return 710;
    }
    return 350;
  };

  let carousel: HTMLElement;
  let carouselIdx = 0;
  let carouselMaxIdx = 0;

  const handleGallery = (to: 'left' | 'right' | number) => {
    if (to === 'left') {
      carousel.scrollTo({
        left: carousel.clientWidth * (carouselIdx - 1),
        behavior: $reducedMotion ? 'instant' : 'smooth'
      });
    } else if (to === 'right') {
      carousel.scrollTo({
        left: carousel.clientWidth * (carouselIdx + 1),
        behavior: $reducedMotion ? 'instant' : 'smooth'
      });
    } else if (typeof to === 'number') {
      carousel.scrollTo({
        left: carousel.clientWidth * to,
        behavior: $reducedMotion ? 'instant' : 'smooth'
      });
    }
  };

  const onPreviewMedia = (media: ShopifyImage | string | undefined, idx?: number) => {
    selectedMedia = media;
    selectedMediaIdx = idx;
    document.body.style.overflow = 'hidden';
  };

  const onClosePreview = (event?: KeyboardEvent | undefined) => {
    if (event && event.key !== 'Escape') {
      return;
    }
    selectedMedia = undefined;
    selectedMediaIdx = undefined;

    document.body.style.overflow = 'visible';
  };

  const handleMediaCarousel = (to: 'left' | 'right') => {
    if (selectedMediaIdx !== undefined) {
      let newSelectedMediaIdx;
      if (to === 'left') {
        newSelectedMediaIdx = selectedMediaIdx - 1;
      } else {
        newSelectedMediaIdx = selectedMediaIdx + 1;
      }

      if (newSelectedMediaIdx > -1 && newSelectedMediaIdx < parsedMedia.length) {
        if (parsedMedia[newSelectedMediaIdx].__typename === 'Image') {
          selectedMedia = parsedMedia[newSelectedMediaIdx] as ShopifyImage;
        } else if (parsedMedia[newSelectedMediaIdx].__typename === 'Video') {
          const src = getSrc((parsedMedia[newSelectedMediaIdx] as ShopifyVideo).sources);

          selectedMedia = src;
        }
        selectedMediaIdx = newSelectedMediaIdx;
      }
    }
  };

  // Reset if images change
  $: if (carousel && images) {
    carousel.scrollTo({ left: 0, behavior: 'instant' });
    carouselIdx = 0;
  }

  // Triggered when 2nd and 3rd image are viewed (1st time)
  $: if (carouselIdx > carouselMaxIdx && onImageView) {
    carouselMaxIdx = carouselIdx;
    onImageView(carouselMaxIdx + 1);
  }

  let imagesByVariant: Map<string, ShopifyImage[]> | undefined = undefined;
  $: imageVariantOptions = getVariantOptions({ images, options });
  let selectedVariantOption = variantColor ? flatOptions(options)[0] : undefined;

  $: if (!!variantColor && !!options.length && imageVariantOptions) {
    const tmp = new Map<string, ShopifyImage[]>();

    imageVariantOptions.forEach((variant) => {
      if (!variant) return;
      const imgs = images.filter((img) => img.altText === variant);
      tmp.set(variant, imgs);
    });

    imagesByVariant = tmp;
  }

  $: parsedImages =
    !!imagesByVariant && !!selectedVariantOption
      ? imagesByVariant.get(selectedVariantOption)
      : images;

  $: filteredMedia = (media || [])
    .map((p) => {
      if (p.__typename === 'Image') {
        const result = parsedImages?.find((item) => item.url === p.url);
        return result ?? p;
      }
      return p;
    })
    .filter(
      (item) =>
        (item.__typename === 'Video' && item.alt && item.alt === DESKTOP_ALT && $aboveLg) ||
        (item.__typename === 'Video' && !item.alt && !$aboveLg) ||
        item.__typename !== 'Video'
    )
    .filter((_, index) => {
      if ((ATestVersionMediaIndexes || []).length > 0) {
        if ($abTestVersion === 'A') {
          return (ATestVersionMediaIndexes || []).includes(index);
        } else {
          return !(ATestVersionMediaIndexes || []).includes(index);
        }
      } else {
        return true;
      }
    });

  // wait for abtestversion to be available if necessaru
  $: parsedMedia =
    (ATestVersionMediaIndexes || []).length === 0 || $abTestVersion ? filteredMedia : [];

  $: length = (parsedMedia || []).length;

  // Reset if images change
  $: if (carousel && images) {
    carousel.scrollTo({ left: 0, behavior: 'instant' });
    carouselIdx = 0;
  }

  // Triggered when 2nd and 3rd image are viewed (1st time)
  $: if (carouselIdx > carouselMaxIdx && onImageView) {
    carouselMaxIdx = carouselIdx;
    onImageView(carouselMaxIdx + 1);
  }

  // Videos

  $: firstVideoIndex = parsedMedia.findIndex((media) => media?.__typename === 'Video');
  $: isCurrMediaAVideo = parsedMedia[carouselIdx]?.__typename == 'Video';

  $: videoElements = [...Array(length)];
  $: videosProgress = [...Array(length)];

  const updateProgress = (event: Event, index: number) => {
    const video = event.target as HTMLVideoElement;
    const currentTime = video.currentTime;
    const duration = video.duration || 0;
    const progress = duration ? Math.round((currentTime / duration) * 100) : 0;

    videosProgress[index] = progress;
  };
</script>

<div class={cn(host({ size }), className, 'overflow-hidden')}>
  {#each imageDescription || [] as info}
    {#if +info.image_number === carouselIdx + 1}
      <div
        class="absolute right-2.5 top-2.5 z-10 w-fit lg:right-4 lg:top-4"
        transition:fade={{ duration: $reducedMotion ? 0 : 200 }}
      >
        <Accordion
          title={info.title}
          variant="image-info"
          class="transform-gpu backdrop-blur"
          onInteract={() => {
            onImageDescriptionInteract && onImageDescriptionInteract(info.title);
          }}
        >
          <div class="border-t border-primary/4 pt-2.5 text-xs">
            <ul class="flex flex-col gap-3">
              <li class="flex items-center justify-between text-primary/40">
                <span>
                  {info.metrics_label}
                </span>
                <span>
                  {info.metrics_value_label}
                </span>
              </li>
              {#each info.metrics || [] as metric}
                <li class={cn('flex items-center justify-between pb-0')}>
                  <span>
                    {metric.name}
                  </span>
                  <span>
                    {metric.value}
                  </span>
                </li>
              {/each}
            </ul>
          </div>
        </Accordion>
      </div>
    {/if}
  {/each}

  <!-- Slider Buttons-->
  {#if length > 1}
    <div class={cn('absolute left-2 top-1/2 -translate-y-1/2 lg:left-3 z-10')}>
      <SliderButton
        aria-label={$t('a11y.image-previous')}
        icon="chevron-left"
        disabled={carouselIdx === 0}
        class={cn(
          sliderButtonStyle({
            size,
            galleryOnly: !(title && category)
          })
        )}
        on:click={() => handleGallery('left')}
      />
    </div>
    <div class={cn('absolute right-2 top-1/2 -translate-y-1/2 lg:right-3 z-10')}>
      <SliderButton
        aria-label={$t('a11y.image-next')}
        icon="chevron-right"
        disabled={carouselIdx >= length - 1}
        class={cn(
          sliderButtonStyle({
            size,
            galleryOnly: !(title && category)
          })
        )}
        on:click={() => handleGallery('right')}
      />
    </div>
  {/if}
  <!-- END Slider Buttons -->

  {#if variantColor?.value && !!options?.length}
    <ColorSelector
      bind:selected={selectedVariantOption}
      metafield={variantColor.value}
      class={cn(colorSelectorStyle({ size }))}
    />
  {/if}

  <!-- Carousel Images -->
  <svelte:element
    this={href ? 'a' : 'div'}
    data-sveltekit-noscroll={false}
    {href}
    class="h-full border-none outline-none"
  >
    <div
      bind:this={carousel}
      use:snapScrollPosition={(i) => {
        carouselIdx = i;
      }}
      use:intersectionObserver={{
        options: {
          // trigger visibility before entering the screen
          rootMargin: '100px 0px 100px 0px'
        },
        callback: (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.remove('[&>*]:hidden');
            } else {
              e.target.classList.add('[&>*]:hidden');
            }
          });
        }
      }}
      class={cn(
        'flex h-full w-full object-cover',
        'scroll snap-x snap-mandatory overflow-x-auto hide-scrollbar'
      )}
    >
      {#if parsedMedia?.length > 0}
        {#each parsedMedia || [] as mediaItem, i}
          {#if mediaItem && mediaItem.__typename === 'Image'}
            {@const { url: src, altText: alt } = mediaItem}
            {@const width = getWidth()}
            <div
              class="min-w-full select-none snap-center snap-always"
              use:intersectionObserver={{
                options: {
                  root: carousel
                },
                callback: (entries) => {
                  entries.forEach((e) => {
                    if (e.isIntersecting) {
                      e.target.classList.remove('[&>*]:hidden');
                    } else {
                      e.target.classList.add('[&>*]:hidden');
                    }
                  });
                }
              }}
            >
              <Link
                class="block w-full h-full"
                title={alt || ''}
                on:click={() => onPreviewMedia(mediaItem, i)}
              >
                <ShopifyImg
                  {src}
                  {alt}
                  widths={[!containFirst || isPdp ? 1000 : width, (w) => (w < width ? w : null)]}
                  sizes={buildSizes({
                    default: `${width}px`,
                    lg: isHighlight || isPdp || !containFirst ? '100vw' : '50vw'
                  })}
                  draggable="false"
                  class={cn(
                    'h-full w-full bg-highlight/64',
                    i === 0 && containFirst ? 'object-contain' : 'object-cover'
                  )}
                />
              </Link>
            </div>
          {/if}

          {#if mediaItem && mediaItem.__typename === 'Video'}
            {@const src = getSrc(mediaItem.sources)}
            <video
              src={src + '#t=0.001'}
              preload="metadata"
              autoplay={true}
              bind:this={videoElements[i]}
              loop
              muted
              playsinline
              on:loadedmetadata={(event) => updateProgress(event, i)}
              on:timeupdate={(event) => updateProgress(event, i)}
              class="min-w-full snap-center snap-always h-full object-cover"
              on:click={() => onPreviewMedia(src, i)}
            />
          {/if}
        {/each}
      {:else}
        <Skeleton class="min-w-full snap-center snap-always h-full object-cover" />
      {/if}
    </div>

    <div class="absolute bottom-0 z-10 w-full">
      <!-- Slider -->
      <div class="w-full flex justify-between relative items-end">
        {#if length > 1}
          <Slider
            total={length}
            currentIndex={carouselIdx}
            {videosProgress}
            class={cn(sliderStyle({ size }))}
          />
        {/if}
        <!-- Play and pause button -->
        {#if firstVideoIndex != -1 && showVideoButton}
          <button
            class="flex h-[28px] w-[28px] items-center justify-center m-2.5 shrink-0 rounded-full bg-highlight/80 border-none transition-all"
            on:click={() => {
              if (videoElements[carouselIdx]?.paused && isCurrMediaAVideo) {
                videoElements[carouselIdx]?.play();
              } else if (!isCurrMediaAVideo) {
                handleGallery(firstVideoIndex);
              } else {
                videoElements[carouselIdx]?.pause();
              }
            }}
          >
            {#if !isCurrMediaAVideo || (isCurrMediaAVideo && videoElements[carouselIdx]?.paused)}
              <Icon icon="play" size="sm" />
            {:else}
              <Icon icon="pause" size="sm" />
            {/if}
          </button>
        {/if}
      </div>

      <!-- Info -->
      {#if title || category}
        <div class="w-full">
          <div class={cn(infoContainerStyle({ size }))}>
            <div class={cn(typographyStyle({ size }))}>
              <h3 aria-label={$t('a11y.product-name')} class="line-clamp-1 text-primary">
                {title}
              </h3>
              <p aria-label={$t('a11y.category')} class="line-clamp-1 text-primary/40 font-medium">
                {category}
              </p>
            </div>

            <div class={cn('text-end lining-nums tabular-nums', typographyStyle({ size }))}>
              {#if currencyCode && price}
                <p aria-label={$t('a11y.price')} class="whitespace-nowrap">
                  {$formatCurrency(currencyCode, +price)}
                </p>
              {/if}
              {#if compareAtPrice && currencyCode}
                {#if isSitewideSale && !!$sitewideSale && price}
                  <p class="font-medium text-accent">
                    {$formatCurrency(currencyCode, $sitewideSale.getDiscountedPrice(+price))}
                  </p>
                {:else if isOnSale}
                  <p
                    aria-label={$t('a11y.before-sale-price')}
                    class="whitespace-nowrap text-primary/40 line-through"
                  >
                    {$formatCurrency(currencyCode, +compareAtPrice)}
                  </p>
                {/if}
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </svelte:element>
</div>

<svelte:window on:keydown={onClosePreview} />

{#if selectedMedia}
  <PreviewMedia
    {selectedMedia}
    on:close={() => onClosePreview()}
    on:next={() => handleMediaCarousel('right')}
    on:prev={() => handleMediaCarousel('left')}
    prevDisabled={selectedMediaIdx === 0}
    nextDisabled={selectedMediaIdx === parsedMedia.length - 1}
  />
{/if}
